import React from "react";

import styles from "./OrderSummary.module.scss";
import {
  CartItem,
  CdekCalculateResponse,
  DeliveryRule,
} from "../../../common/types/interfaces";
import { DeliveryService, DeliveryType } from "../../../common/types/enums";

interface Props {
  orderItems: CartItem[];
  remainingItems: CartItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  getSaleItemName: (id: number) => string;
  discountAmount?: number;
  bonusesSpent?: number;
  bonusesToAdd?: number;
  hasPromo?: boolean;
  deliveryType?: DeliveryType;
  bestDeliveryText: string | null;
  storeName?: string;
  currentBestRule?: DeliveryRule | null;
  nextBestRule?: DeliveryRule | null;
  bestPossibleRule?: DeliveryRule | null;
  deliveryAvailable?: boolean;
  deliveryError?: string | null;
  selectedAddressId?: number | null;
  deliveryService?: DeliveryService;
  cdekOptions?: CdekCalculateResponse | null;
  cutleryCount?: number;
  giftItemName?: string;
}

const fmt = (value: number) => Number(value).toLocaleString("ru-RU");

export const OrderSummary: React.FC<Props> = ({
  orderItems,
  remainingItems,
  subtotal,
  deliveryFee,
  total,
  getSaleItemName,
  discountAmount = 0,
  bonusesSpent = 0,
  bonusesToAdd = 0,
  hasPromo = false,
  deliveryType,
  bestDeliveryText,
  storeName,
  currentBestRule,
  nextBestRule,
  bestPossibleRule,
  deliveryAvailable,
  deliveryError,
  selectedAddressId,
  deliveryService,
  cdekOptions,
  cutleryCount,
  giftItemName,
}) => {
  const totalWithoutDiscounts = subtotal + deliveryFee;
  const savings = totalWithoutDiscounts - total - bonusesSpent;
  const isCdekDelivery = deliveryService === DeliveryService.CDEK;
  const showBestConditions =
    !isCdekDelivery && (currentBestRule || nextBestRule || bestPossibleRule);
  const hasCdekOptions = !!cdekOptions?.tariffs?.length;
  const showCutlery = cutleryCount !== undefined && cutleryCount > 0;

  const renderItems = () => (
    <div className={styles.Items}>
      {orderItems.map((item) => (
        <div key={item.id} className={styles.ItemRow}>
          <span className={styles.ItemName}>
            {getSaleItemName(Number(item.saleItemId))}
          </span>
          <span className={styles.ItemQty}>{item.quantity} ×</span>
          <span className={styles.ItemPrice}>{fmt(item.price)} ₽</span>
        </div>
      ))}

      {giftItemName && (
        <div className={styles.ItemRow}>
          <span className={`${styles.ItemName} ${styles.Gift}`}>
            🎁 {giftItemName}
          </span>
          <span className={styles.ItemQty}>1 ×</span>
          <span className={`${styles.ItemPrice} ${styles.Gift}`}>0 ₽</span>
        </div>
      )}

      {showCutlery && (
        <div className={styles.ItemRow}>
          <span className={styles.ItemName}>🍴 Приборы</span>
          <span className={styles.ItemQty}>{cutleryCount} ×</span>
          <span className={styles.ItemPrice}>0 ₽</span>
        </div>
      )}
    </div>
  );

  const renderTotals = () => (
    <>
      <div className={styles.Row}>
        <span className={styles.RowLabel}>Подытог</span>
        <span className={styles.RowValue}>{fmt(subtotal)} ₽</span>
      </div>

      {discountAmount > 0 && (
        <div className={styles.Row}>
          <span className={styles.RowLabel}>
            Скидка{hasPromo && <span className={styles.Promo}>Промо</span>}
          </span>
          <span className={`${styles.RowValue} ${styles.Accent}`}>
            −{fmt(discountAmount)} ₽
          </span>
        </div>
      )}

      {bonusesSpent > 0 && (
        <div className={styles.Row}>
          <span className={styles.RowLabel}>Списано бонусами</span>
          <span className={`${styles.RowValue} ${styles.Accent}`}>
            −{fmt(bonusesSpent)} ₽
          </span>
        </div>
      )}

      <div className={styles.Row}>
        <span className={styles.RowLabel}>
          {isCdekDelivery ? "Доставка СДЭК" : "Доставка"}
        </span>
        <span className={styles.RowValue}>
          {deliveryFee === 0 && !isCdekDelivery
            ? "Бесплатно"
            : `${fmt(deliveryFee)} ₽`}
        </span>
      </div>

      {bonusesToAdd > 0 && (
        <div className={styles.Row}>
          <span className={styles.RowLabel}>Начислим бонусов</span>
          <span className={`${styles.RowValue} ${styles.Accent}`}>
            +{fmt(bonusesToAdd)} ₽
          </span>
        </div>
      )}

      <div className={styles.TotalRow}>
        <div className={styles.TotalLeft}>
          <span className={styles.TotalLabel}>Итого</span>
          {savings > 0 && (
            <span className={styles.Savings}>
              Экономия {fmt(savings)} ₽
            </span>
          )}
        </div>
        <span className={styles.TotalValue}>{fmt(total)} ₽</span>
      </div>
    </>
  );

  if (deliveryType === DeliveryType.DELIVERY) {
    if (!selectedAddressId) {
      return (
        <div className={styles.Container}>
          <h3 className={styles.Title}>Состав заказа</h3>
          <div className={styles.Notice}>
            <h4 className={styles.NoticeTitle}>Выберите адрес доставки</h4>
            <p className={styles.NoticeText}>
              Пожалуйста, выберите или добавьте адрес для расчёта доставки.
            </p>
          </div>
        </div>
      );
    }

    if (!isCdekDelivery && !hasCdekOptions && deliveryAvailable === false) {
      return (
        <div className={styles.Container}>
          <h3 className={styles.Title}>Состав заказа</h3>
          <div className={`${styles.Notice} ${styles.NoticeError}`}>
            <h4 className={styles.NoticeTitle}>Доставка недоступна</h4>
            <p className={styles.NoticeText}>
              {deliveryError || "К сожалению, мы не доставляем по этому адресу."}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.Container}>
        <h3 className={styles.Title}>Состав заказа</h3>
        {renderItems()}

        {remainingItems.length > 0 && (
          <div className={styles.Notice}>
            <h4 className={styles.NoticeTitle}>
              Недоступны в {storeName || "выбранном магазине"}
            </h4>
            {remainingItems.map((item) => (
              <div key={item.id} className={styles.RemainingRow}>
                <span className={styles.ItemName}>
                  {getSaleItemName(Number(item.saleItemId))}
                </span>
                <span className={styles.ItemQty}>{item.quantity} шт</span>
              </div>
            ))}
            <p className={styles.NoticeText}>
              Эти товары останутся в корзине.
            </p>
          </div>
        )}

        {renderTotals()}

        {showBestConditions && (
          <div className={styles.DeliveryInfo}>
            {currentBestRule && (
              <div className={styles.InfoRow}>
                <span>Текущие условия</span>
                <span className={styles.Accent}>
                  {currentBestRule.value === 0
                    ? "Бесплатно"
                    : `${currentBestRule.value} ₽`}
                </span>
              </div>
            )}
            {nextBestRule && (
              <div className={styles.InfoRow}>
                <span>До следующей цели</span>
                <span className={styles.Accent}>
                  +{nextBestRule.level - subtotal} ₽
                </span>
              </div>
            )}
            {bestPossibleRule &&
              bestPossibleRule.value !== currentBestRule?.value && (
                <div className={styles.InfoRow}>
                  <span>Лучшие условия</span>
                  <span className={styles.Accent}>
                    {bestPossibleRule.value === 0
                      ? "Бесплатно"
                      : `${bestPossibleRule.value} ₽`}
                  </span>
                </div>
              )}
          </div>
        )}

        {!isCdekDelivery && bestDeliveryText && (
          <p className={styles.Note}>{bestDeliveryText}</p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <h3 className={styles.Title}>Состав заказа</h3>
      {renderItems()}

      {remainingItems.length > 0 && (
        <div className={styles.Notice}>
          <h4 className={styles.NoticeTitle}>
            Недоступны в {storeName || "выбранном магазине"}
          </h4>
          {remainingItems.map((item) => (
            <div key={item.id} className={styles.RemainingRow}>
              <span className={styles.ItemName}>
                {getSaleItemName(Number(item.saleItemId))}
              </span>
              <span className={styles.ItemQty}>{item.quantity} шт</span>
            </div>
          ))}
        </div>
      )}

      {renderTotals()}
    </div>
  );
};